import { debounce } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { RxCross2, RxMagnifyingGlass } from 'react-icons/rx'
import { ShortTextInput } from '~/@/components/inputs/text/short-text-input'
import { Button } from '~/@/components/ui/button'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { trackTableSearch } from '~/common/utils/metrics'

interface ToggleableSearchFieldProps {
  onSearch: (newSearchString?: string) => void
  searchString?: string
  tableName?: string
  clearSearchString?: boolean
  showPlaceholderWhenCollapsed?: boolean
}

export function ToggleableSearchField({
  onSearch,
  searchString,
  tableName,
  clearSearchString,
  showPlaceholderWhenCollapsed,
}: ToggleableSearchFieldProps) {
  // Start expanded when a search string is already active (e.g. restored from local storage)
  const [showSearchField, setShowSearchField] = useState(!!searchString)
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useTypedTranslation()
  const initialLoad = useRef(true)

  // Clear the search field if the clearSearchString prop is true and the searchString is empty
  useEffect(() => {
    if (inputRef.current && clearSearchString && searchString === '') {
      inputRef.current.value = ''
    }
  }, [clearSearchString, searchString])

  // Create a memoized debounced function.
  const debouncedSearch = useMemo(() => {
    return debounce((value: string) => {
      // On initial load, ignore no-op changes only.
      if (initialLoad.current && (value === searchString || (value === '' && !searchString))) {
        return
      }
      onSearch(value)
      initialLoad.current = false
      if (tableName) {
        trackTableSearch({ searchTerm: value, tableName })
      }
    }, 200)
  }, [onSearch, searchString, tableName])

  // Toggle the search field
  const handleSearchFieldToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    setShowSearchField((prevShow) => {
      if (prevShow && inputRef.current) {
        // Clear search field if toggling to hide
        inputRef.current.value = ''
        debouncedSearch('')
      }
      return !prevShow
    })
  }

  // Handle input change and call the debounced search function with the new value
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value)
  }

  return (
    <div className="inline-flex items-center">
      {showSearchField ? (
        <ShortTextInput
          ref={inputRef}
          autoFocus
          className="text-xs"
          defaultValue={searchString}
          endAdornment={
            <button
              className="text-grey-10 flex items-center justify-center text-sm"
              onClick={handleSearchFieldToggle}
            >
              <RxCross2 />
            </button>
          }
          onChange={handleSearch}
          onClick={(event) => event.stopPropagation()}
          placeholder={t('common.search_placeholder')}
          startAdornment={<RxMagnifyingGlass size={16} />}
        />
      ) : (
        <Button onClick={handleSearchFieldToggle} size="xs" type="button" variant="ghost">
          <RxMagnifyingGlass size={16} />
          {showPlaceholderWhenCollapsed && (
            <span className="text-xs">{t('common.search_placeholder')}</span>
          )}
        </Button>
      )}
    </div>
  )
}
